import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from './supabase';

const STORAGE_KEY = 'nuled.project_list.v1';

export type ProjectItem = {
  price_pending?: boolean;
  product_id: string;
  sku: string;
  name: string;
  brand_code: string;
  msrp_mxn?: number | null;     // display MXN (legacy)
  msrp_usd?: number | null;     // PVP USD source-of-truth
  cost_usd?: number | null;     // tu costo neto USD (con tier)
  image_url?: string | null;
  qty: number;
  unit?: string;
  notes?: string;
};

type Ctx = {
  items: ProjectItem[];
  count: number;
  total_mxn: number;
  add: (it: Omit<ProjectItem, 'qty'> & { qty?: number }) => void;
  setQty: (sku: string, qty: number) => void;
  setNotes: (sku: string, notes: string) => void;
  remove: (sku: string) => void;
  clear: () => void;
  open: boolean;
  setOpen: (b: boolean) => void;
};

const ProjectCtx = createContext<Ctx | null>(null);

export function ProjectListProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ProjectItem[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as ProjectItem[]) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  // Hidratar precios faltantes: si algún item tiene msrp_usd null/0 y no tiene cost_usd,
  // hacer query a products_public por product_id (o sku canonical) y rellenar el precio.
  // Esto resuelve el bug de variantes que se persistieron en localStorage sin precio.
  useEffect(() => {
    const needsPrice = items.filter(it =>
      !it.price_pending &&
      (it.msrp_usd == null || it.msrp_usd === 0) &&
      (it.cost_usd == null || it.cost_usd === 0)
    );
    if (needsPrice.length === 0) return;

    (async () => {
      // Intentar primero por product_id (más confiable)
      const productIds = needsPrice.map(it => it.product_id).filter(Boolean);
      if (productIds.length === 0) return;

      const { data: prods } = await supabase
        .from('products_public')
        .select('id, sku, msrp_usd, msrp_mxn, images')
        .in('id', productIds);
      if (!prods || prods.length === 0) return;

      const byId: Record<string, { msrp_usd: number | null; msrp_mxn: number | null; image_url: string | null }> = {};
      for (const p of prods as any[]) {
        const img = p.images?.find((i: any) => i.is_thumbnail) ?? p.images?.[0];
        byId[p.id] = {
          msrp_usd: p.msrp_usd ? Number(p.msrp_usd) : null,
          msrp_mxn: p.msrp_mxn ? Number(p.msrp_mxn) : null,
          image_url: img?.url ?? null,
        };
      }

      setItems(prev => prev.map(it => {
        if ((it.msrp_usd != null && it.msrp_usd > 0) || (it.cost_usd != null && it.cost_usd > 0)) return it;
        if (it.price_pending) return it;
        const fix = byId[it.product_id];
        if (!fix) return it;
        return {
          ...it,
          msrp_usd: fix.msrp_usd ?? it.msrp_usd,
          msrp_mxn: fix.msrp_mxn ?? it.msrp_mxn,
          image_url: it.image_url ?? fix.image_url,
        };
      }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);  // solo al cambiar el número de items, no en cada update

  const add = useCallback((it: Omit<ProjectItem, 'qty'> & { qty?: number }) => {
    setItems(prev => {
      const existing = prev.find(x => x.sku === it.sku);
      if (existing) {
        return prev.map(x => x.sku === it.sku ? { ...x, qty: x.qty + (it.qty ?? 1) } : x);
      }
      return [...prev, { ...it, qty: it.qty ?? 1 }];
    });
    setOpen(true);
  }, []);

  const setQty = useCallback((sku: string, qty: number) => {
    setItems(prev => prev.map(x => x.sku === sku ? { ...x, qty: Math.max(1, qty) } : x));
  }, []);

  const setNotes = useCallback((sku: string, notes: string) => {
    setItems(prev => prev.map(x => x.sku === sku ? { ...x, notes } : x));
  }, []);

  const remove = useCallback((sku: string) => {
    setItems(prev => prev.filter(x => x.sku !== sku));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const total_mxn = items.reduce((sum, it) => sum + (it.msrp_mxn ?? 0) * it.qty, 0);

  return (
    <ProjectCtx.Provider value={{
      items,
      count: items.reduce((n, it) => n + it.qty, 0),
      total_mxn,
      add, setQty, setNotes, remove, clear,
      open, setOpen,
    }}>
      {children}
    </ProjectCtx.Provider>
  );
}

export function useProjectList() {
  const v = useContext(ProjectCtx);
  if (!v) throw new Error('useProjectList fuera de ProjectListProvider');
  return v;
}
